import { CourseRole } from "../../shared/types/roles";

import {
  courseGroupingUserSelectors,
  courseSelectors,
  courseUserSelectors,
  createAppSelector,
  createShallowRecordSelector,
} from "./base";
import { selectCourseForSectionId, selectCurrentCourse } from "./current";
import { selectIsCurrentOrgUserDisabled, selectIsOrgUserDisabled } from "./org";

import type { CourseGroupingUserType, CourseType, CourseUserType } from "components/server-types";
import { getEnabledCourseRole } from "utils/roles";

// Course-admin status is derived from a CourseUser row for the course or a CourseGroupingUser
// row for the course's grouping. Org-disabled membership contributes no course role
// (mirrors CourseUser / CourseGroupingUser.enabled()).
export const getCourseRole = (
  courseUsers: CourseUserType[],
  courseGroupingUsers: CourseGroupingUserType[],
  course: CourseType | null,
  isOrgUserDisabled = false,
) => {
  if (!course) {
    return null;
  }

  const courseUser = courseUsers.find((cu) => cu.course_id === course.id);
  if (courseUser) {
    return getEnabledCourseRole(courseUser, isOrgUserDisabled);
  }

  const groupingUser = courseGroupingUsers.find(
    (gu) => gu.course_grouping_id === course.grouping_id,
  );
  return getEnabledCourseRole(groupingUser ?? null, isOrgUserDisabled);
};

/** Enabled course role by course id for the current user (omits null / disabled). */
export const selectCourseRoleByCourseIdMap = createShallowRecordSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    courseSelectors.selectAll,
    selectIsCurrentOrgUserDisabled,
  ],
  (courseUsers, courseGroupingUsers, courses, isOrgUserDisabled): Record<string, CourseRole> => {
    const roleByCourseId: Record<string, CourseRole> = {};

    for (const courseUser of courseUsers) {
      const role = getEnabledCourseRole(courseUser, isOrgUserDisabled);
      if (role) {
        roleByCourseId[courseUser.course_id] = role;
      }
    }

    if (courseGroupingUsers.length === 0) {
      return roleByCourseId;
    }

    const groupingUserByGroupingId = new Map(
      courseGroupingUsers.map((groupingUser) => [groupingUser.course_grouping_id, groupingUser]),
    );
    for (const course of courses) {
      if (course.id in roleByCourseId || !course.grouping_id) {
        continue; // eslint-disable-line no-continue
      }
      const role = getEnabledCourseRole(
        groupingUserByGroupingId.get(course.grouping_id) ?? null,
        isOrgUserDisabled,
      );
      if (role) {
        roleByCourseId[course.id] = role;
      }
    }
    return roleByCourseId;
  },
);

export const selectCurrentCourseRole = createAppSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    selectCurrentCourse,
    selectIsCurrentOrgUserDisabled,
  ],
  (courseUsers, courseGroupingUsers, course, isOrgUserDisabled) =>
    getCourseRole(courseUsers, courseGroupingUsers, course, isOrgUserDisabled),
);

export const selectCourseRoleByUserId = createAppSelector(
  [
    courseUserSelectors.filterByUserId,
    courseGroupingUserSelectors.filterByUserId,
    selectCurrentCourse,
    selectIsOrgUserDisabled,
  ],
  (courseUsers, courseGroupingUsers, course, isOrgUserDisabled) =>
    getCourseRole(courseUsers, courseGroupingUsers, course, isOrgUserDisabled),
);

export const selectCourseRoleByCourseId = createAppSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    courseSelectors.selectById,
    selectIsCurrentOrgUserDisabled,
  ],
  (courseUsers, courseGroupingUsers, course, isOrgUserDisabled) =>
    getCourseRole(courseUsers, courseGroupingUsers, course, isOrgUserDisabled),
);

export const selectCourseRoleBySectionId = createAppSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    selectCourseForSectionId,
    selectIsCurrentOrgUserDisabled,
  ],
  (courseUsers, courseGroupingUsers, course, isOrgUserDisabled) =>
    getCourseRole(courseUsers, courseGroupingUsers, course, isOrgUserDisabled),
);

// True if the user has any CourseUser rows. Course-admin status is now derived from
// CourseUser presence rather than an OrgUser.role of "course_admin".
export const selectIsUserACourseAdmin = createAppSelector(
  [courseUserSelectors.filterByUserId],
  (courseUsers) => courseUsers.length > 0,
);
